<template>
  <div class="w-64">
    <template v-if="view === 'months'">
      <div class="mb-2 flex items-center justify-between">
        <button
          type="button"
          class="scroll-button"
          :disabled="displayedYear <= minYear"
          :aria-label="t('previousYear')"
          @click="displayedYear--"
        >
          <ArrowLeftIcon class="size-4" />
        </button>
        <button
          type="button"
          class="rounded-full px-2 py-0.5 text-sm font-bold uppercase text-hazel-500 hover:bg-obsidian-100"
          data-testid="datepicker-year-toggle"
          @click="view = 'years'"
        >
          {{ displayedYear }}
        </button>
        <button
          type="button"
          class="scroll-button"
          :disabled="displayedYear >= maxYear"
          :aria-label="t('nextYear')"
          @click="displayedYear++"
        >
          <ArrowRightIcon class="size-4" />
        </button>
      </div>
      <div class="grid grid-cols-3 gap-1">
        <button
          v-for="monthIndex in 12"
          :key="monthIndex"
          type="button"
          class="option"
          :class="{ selected: isSelectedMonth(monthIndex - 1) }"
          :disabled="isMonthDisabled(monthIndex - 1)"
          :data-testid="`datepicker-month-${monthIndex - 1}`"
          @click="$emit('monthSelected', { month: monthIndex - 1, year: displayedYear })"
        >
          {{ getMonthName(monthIndex - 1) }}
        </button>
      </div>
    </template>
    <div v-else ref="yearList" class="grid max-h-64 grid-cols-4 gap-1 overflow-y-auto">
      <button
        v-for="yearOption in years"
        :key="yearOption"
        type="button"
        class="option"
        :class="{ selected: yearOption === selectedYear }"
        :data-year="yearOption"
        :data-testid="`datepicker-year-${yearOption}`"
        @click="handleYearSelect(yearOption)"
      >
        {{ yearOption }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-vue-next';
import { isMonthOutOfBounds, type MonthValue } from './datepicker-utils';

const props = defineProps<{
  year: number;
  selectedMonth?: MonthValue | null;
  selectedYear?: number | null;
  minDate?: Date | null;
  maxDate?: Date | null;
  /** Stop at the year grid and emit `yearSelected` instead of switching to the month grid. */
  yearOnly?: boolean;
}>();

const emit = defineEmits<{
  (e: 'monthSelected', value: MonthValue): void;
  (e: 'yearSelected', year: number): void;
}>();

const view = defineModel<'months' | 'years'>('view', { default: 'months' });

const { t, locale } = useI18n();

const displayedYear = ref(props.year);
const yearList = useTemplateRef<HTMLElement>('yearList');

const currentYear = new Date().getFullYear();
const minYear = computed(() => props.minDate?.getFullYear() ?? currentYear - 120);
const maxYear = computed(() => props.maxDate?.getFullYear() ?? currentYear + 20);
const years = computed(() =>
  Array.from({ length: maxYear.value - minYear.value + 1 }, (_, i) => minYear.value + i),
);

const getMonthName = (month: number) =>
  new Date(2020, month, 1).toLocaleString([locale.value], { month: 'short' });

const isSelectedMonth = (month: number) =>
  props.selectedMonth?.month === month && props.selectedMonth.year === displayedYear.value;

const isMonthDisabled = (month: number) =>
  isMonthOutOfBounds(
    { month, year: displayedYear.value },
    { minDate: props.minDate, maxDate: props.maxDate },
  );

const handleYearSelect = (year: number) => {
  if (props.yearOnly) {
    emit('yearSelected', year);
    return;
  }
  displayedYear.value = year;
  view.value = 'months';
};

watch(
  view,
  async (value) => {
    if (value !== 'years') return;
    await nextTick();
    const target = props.selectedYear ?? displayedYear.value;
    yearList.value
      ?.querySelector<HTMLElement>(`[data-year="${target}"]`)
      ?.scrollIntoView({ block: 'center' });
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.scroll-button {
  @apply rounded-full p-2 hover:bg-obsidian-600 hover:text-white disabled:pointer-events-none disabled:opacity-30;
}

.option {
  @apply rounded-lg p-2 text-sm hover:bg-obsidian-100 disabled:pointer-events-none disabled:text-obsidian-400;

  &.selected {
    @apply bg-obsidian-600 text-white;
  }
}
</style>

<i18n lang="json">
{
  "en": {
    "previousYear": "Previous year",
    "nextYear": "Next year"
  },
  "de": {
    "previousYear": "Vorheriges Jahr",
    "nextYear": "Nächstes Jahr"
  },
  "es": {
    "previousYear": "Año anterior",
    "nextYear": "Año siguiente"
  }
}
</i18n>
